/**
 * Images fetched as they are first needed and kept (ADR-094 decision 27: a preview loads only what it shows): the
 * Look panel's preview draws the published piece sheets from one, and the character sprite viewer its page images.
 * Nothing is fetched until it is asked for, and an image that has not arrived is answered as none, so a painter
 * leaves its layer out for that frame. Each image is fetched in CORS mode, as the world's sheet loader
 * (`looks/look-sheets.ts`) fetches the same addresses: the asset base may be another origin whose answers are cached
 * for a year without varying on the request's origin, so a sheet the preview fetched without CORS would be kept in the
 * browser's cache in a form the world's load then refuses.
 */
export class LazyImages {
  private readonly held = new Map<string, HTMLImageElement>();

  /**
   * Holds no image until one is asked for.
   *
   * @param base The asset base the paths are under.
   * @param arrived Called once each time an image asked for has loaded and decoded, so whoever paints only on a
   *   change can paint again; one that paints every frame gives none.
   */
  public constructor(
    private readonly base: string,
    private readonly arrived?: () => void,
  ) {}

  /**
   * The image at a path, asked for on first use.
   *
   * @param path The path under the asset base.
   * @returns The image when it has arrived and decoded, else null.
   */
  public get(path: string): HTMLImageElement | null {
    let image = this.held.get(path);
    if (image === undefined) {
      image = new Image();
      image.crossOrigin = "anonymous";
      if (this.arrived !== undefined) {
        image.addEventListener("load", this.arrived, { once: true });
      }
      image.src = `${this.base}/${path}`;
      this.held.set(path, image);
    }
    return image.complete && image.naturalWidth > 0 ? image : null;
  }

  /**
   * The paths asked for so far, for tests and rigs.
   *
   * @returns The paths, in the order they were first asked for.
   */
  public get asked(): string[] {
    return [...this.held.keys()];
  }
}
